import { BarChart3, Image as ImageIcon, Plus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { ApiError } from '@/api/client'
import { mediaApi } from '@/api/endpoints'
import type { AuthorKind, Media, Post, ReplyPolicy } from '@/api/types'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { PixelSpinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { useT, useTx } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { useCurrentUser } from '@/features/auth'
import { ChallengeDialog, IdentityPicker } from '@/features/identity'
import { cn } from '@/lib/cn'
import { countChars } from '@/lib/text'
import { useDraftStore } from '@/stores/composer'

import { useCreatePost } from '../hooks/useCreatePost'
import { QuoteCard } from './QuoteCard'
import { ReplyPolicyPicker } from './ReplyPolicyPicker'

const MAX_CHARS = 280
const MAX_MEDIA = 4
/** 和后端的 VIDEO_MAX_BYTES 一致（D92）：超了就别白传 100 MB 再被拒 */
const VIDEO_MAX_MB = 100
const VIDEO_POLL_MS = 2000
const MEDIA_ACCEPT =
  'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm,video/x-matroska,.mkv'

function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/') || /\.(mp4|m4v|mov|webm|mkv)$/i.test(file.name)
}
const POLL_MAX_OPTIONS = 4
const POLL_OPTION_MAX = 25
/** 投票时长的几个档位（分钟）。后端接受 5 分钟到 7 天之间的任意值 */
const POLL_DURATIONS: { minutes: number; label: MessageKey }[] = [
  { minutes: 60, label: 'poll.d1h' },
  { minutes: 1440, label: 'poll.d1d' },
  { minutes: 4320, label: 'poll.d3d' },
  { minutes: 10080, label: 'poll.d7d' },
]

interface Props {
  replyTo?: Post | null
  quoteOf?: Post | null
  autoFocus?: boolean
  placeholder?: string
  onDone?: (post: Post) => void
  compact?: boolean
}

interface Attachment {
  localId: string
  file: File
  previewUrl: string
  media?: Media
  error?: string
  uploading: boolean
  isVideo?: boolean
  /** 视频上传进度 0–1 */
  progress?: number
}

export function Composer({
  replyTo = null,
  quoteOf = null,
  autoFocus = false,
  placeholder,
  onDone,
  compact = false,
}: Props) {
  const { user } = useCurrentUser()
  const toast = useToast()
  const createPost = useCreatePost()
  const t = useT()
  const tx = useTx()

  const draftKey = replyTo ? `reply:${replyTo.id}` : quoteOf ? `quote:${quoteOf.id}` : 'home'
  const draft = useDraftStore((state) => state.drafts[draftKey] ?? '')
  const setDraft = useDraftStore((state) => state.setDraft)
  const clearDraft = useDraftStore((state) => state.clearDraft)

  const [attachments, setAttachments] = useState<Attachment[]>([])
  // 身份选择：默认跟着账号类型走（agent 账号默认 agent，人类默认 human）
  const [authorKind, setAuthorKind] = useState<AuthorKind>(
    user?.account_type === 'agent' ? 'agent' : 'human',
  )
  // 谁可以回复：只有新线程能设，回复沿用线程的设置（D65）
  const [replyPolicy, setReplyPolicy] = useState<ReplyPolicy>('everyone')
  // 投票（D78）：和图片、引用三选一
  const [pollOptions, setPollOptions] = useState<string[] | null>(null)
  const [pollMinutes, setPollMinutes] = useState(1440)
  const [challengeOpen, setChallengeOpen] = useState(false)
  const [verificationToken, setVerificationToken] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const used = countChars(draft)
  const remaining = MAX_CHARS - used
  // 视频转码完才能发（D93）：处理中也算「还没好」
  const uploading = attachments.some(
    (a) => a.uploading || (a.isVideo && a.media?.status === 'processing'),
  )
  const hasVideo = attachments.some((a) => a.isVideo)
  const isAgentAccount = user?.account_type === 'agent'
  // 浏览器里声明自己是 AI 必须先过机器证明（见 design/09-agent-identity.md §2）
  const needsVerification = authorKind === 'agent' && !verificationToken

  const pickIdentity = (next: AuthorKind) => {
    setAuthorKind(next)
    if (next === 'agent' && !verificationToken) setChallengeOpen(true)
  }

  const readyMediaIds = attachments.filter((a) => a.media).map((a) => a.media!.id)
  const trimmedOptions = pollOptions?.map((o) => o.trim()) ?? []
  const pollValid =
    pollOptions === null ||
    (trimmedOptions.every((o) => o.length > 0 && countChars(o) <= POLL_OPTION_MAX) &&
      new Set(trimmedOptions.map((o) => o.toLowerCase())).size === trimmedOptions.length)
  const canSubmit =
    !createPost.isPending &&
    !uploading &&
    remaining >= 0 &&
    !needsVerification &&
    pollValid &&
    (draft.trim().length > 0 ||
      readyMediaIds.length > 0 ||
      Boolean(quoteOf) ||
      pollOptions !== null)

  // 文本域自动增高
  useEffect(() => {
    const node = textareaRef.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, 480)}px`
  }, [draft])

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus()
  }, [autoFocus])

  // 组件卸载时回收对象 URL
  useEffect(
    () => () => {
      attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // 还在轮询处理状态的视频；移除附件时从这里删掉，轮询就停
  const pollingRef = useRef(new Set<string>())

  const patchAttachment = (localId: string, patch: Partial<Attachment>) =>
    setAttachments((current) =>
      current.map((a) => (a.localId === localId ? { ...a, ...patch } : a)),
    )

  const addVideo = async (file: File) => {
    const attachment: Attachment = {
      localId: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      uploading: true,
      isVideo: true,
      progress: 0,
    }
    setAttachments([attachment])
    try {
      let media = await mediaApi.uploadWithProgress(file, (progress) =>
        patchAttachment(attachment.localId, { progress }),
      )
      patchAttachment(attachment.localId, { media, uploading: false })
      pollingRef.current.add(attachment.localId)
      while (media.status === 'processing' && pollingRef.current.has(attachment.localId)) {
        await new Promise((resolve) => setTimeout(resolve, VIDEO_POLL_MS))
        media = await mediaApi.get(media.id)
        if (pollingRef.current.has(attachment.localId)) {
          patchAttachment(attachment.localId, { media })
        }
      }
      pollingRef.current.delete(attachment.localId)
      if (media.status === 'failed') {
        patchAttachment(attachment.localId, { media: undefined, error: t('composer.videoFailed') })
        toast.error(t('composer.videoFailed'))
      }
    } catch (error) {
      pollingRef.current.delete(attachment.localId)
      const message = error instanceof ApiError ? error.message : t('composer.uploadFailed')
      patchAttachment(attachment.localId, { uploading: false, error: message })
      toast.error(message)
    }
  }

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return
    const all = Array.from(files)
    // 一条帖子要么 1–4 张图，要么正好一个视频（D92）
    if (all.some(isVideoFile) || hasVideo) {
      if (all.length > 1 || attachments.length > 0) {
        toast.error(t('composer.videoAlone'))
        return
      }
      const [file] = all
      if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
        toast.error(t('composer.videoTooBig', { max: VIDEO_MAX_MB }))
        return
      }
      await addVideo(file)
      return
    }
    const room = MAX_MEDIA - attachments.length
    if (room <= 0) {
      toast.error(t('composer.tooManyImages', { max: MAX_MEDIA }))
      return
    }

    const picked = Array.from(files).slice(0, room)
    const pending: Attachment[] = picked.map((file) => ({
      localId: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      uploading: true,
    }))
    setAttachments((current) => [...current, ...pending])

    await Promise.all(
      pending.map(async (attachment) => {
        try {
          const media = await mediaApi.upload(attachment.file)
          setAttachments((current) =>
            current.map((a) =>
              a.localId === attachment.localId ? { ...a, media, uploading: false } : a,
            ),
          )
        } catch (error) {
          const message = error instanceof ApiError ? error.message : t('composer.uploadFailed')
          setAttachments((current) =>
            current.map((a) =>
              a.localId === attachment.localId ? { ...a, uploading: false, error: message } : a,
            ),
          )
          toast.error(message)
        }
      }),
    )
  }

  const removeAttachment = (localId: string) => {
    pollingRef.current.delete(localId)
    setAttachments((current) => {
      const target = current.find((a) => a.localId === localId)
      if (target) {
        URL.revokeObjectURL(target.previewUrl)
        // 已上传但未发布的媒体：顺手删掉，免得留垃圾等 GC
        if (target.media) mediaApi.remove(target.media.id).catch(() => {})
      }
      return current.filter((a) => a.localId !== localId)
    })
  }

  const submit = () => {
    if (!canSubmit) return
    createPost.mutate(
      {
        content: draft.trim() || undefined,
        media_ids: readyMediaIds.length ? readyMediaIds : undefined,
        reply_to_id: replyTo?.id,
        quote_of_id: quoteOf?.id,
        author_kind: authorKind,
        verification_token: verificationToken ?? undefined,
        reply_policy: replyTo ? undefined : replyPolicy,
        poll: pollOptions ? { options: trimmedOptions, duration_minutes: pollMinutes } : undefined,
      },
      {
        onSuccess: (post) => {
          // 令牌是一次性的，用掉就得重新验
          setVerificationToken(null)
          if (authorKind === 'agent') setAuthorKind(isAgentAccount ? 'agent' : 'human')
          clearDraft(draftKey)
          setReplyPolicy('everyone')
          setPollOptions(null)
          attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl))
          setAttachments([])
          onDone?.(post)
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.message : t('composer.publishFailed'))
        },
      },
    )
  }

  if (!user) return null

  const effectivePlaceholder =
    placeholder ??
    (replyTo
      ? t('composer.replyPlaceholder')
      : quoteOf
        ? t('composer.quotePlaceholder')
        : t('composer.placeholder'))

  return (
    <div className={cn('flex gap-3 px-4', compact ? 'py-2' : 'py-3')}>
      <Avatar src={user.avatar_url} alt={user.display_name} to={`/${user.username}`} />

      <div className="min-w-0 flex-1">
        {replyTo && (
          <p className="mb-1 text-[15px] text-ink-muted">
            {tx('post.replyingToUser', {
              user: <span className="text-brand-ink">@{replyTo.author.username}</span>,
            })}
          </p>
        )}

        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(event) => setDraft(draftKey, event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') submit()
          }}
          placeholder={effectivePlaceholder}
          aria-label={effectivePlaceholder}
          rows={compact ? 1 : 2}
          className="w-full resize-none bg-transparent text-xl outline-none placeholder:text-ink-faint"
        />

        {attachments.length > 0 && (
          <div
            className={cn(
              'mt-2 grid gap-2',
              attachments.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
            )}
          >
            {attachments.map((attachment) => (
              <div
                key={attachment.localId}
                className="relative overflow-hidden rounded-md border border-line"
              >
                {attachment.isVideo ? (
                  // 本地预览：浏览器会显示第一帧
                  <video
                    src={attachment.previewUrl}
                    muted
                    playsInline
                    preload="metadata"
                    className="max-h-64 w-full bg-black object-contain"
                  />
                ) : (
                  <img
                    src={attachment.previewUrl}
                    alt=""
                    className="max-h-64 w-full object-cover"
                  />
                )}
                {attachment.isVideo && !attachment.error && attachment.uploading && (
                  <VideoProgress
                    label={t('composer.videoUploading', {
                      percent: Math.round((attachment.progress ?? 0) * 100),
                    })}
                    fraction={attachment.progress ?? 0}
                  />
                )}
                {attachment.isVideo && attachment.media?.status === 'processing' && (
                  <VideoProgress label={t('composer.videoProcessing')} />
                )}
                {!attachment.isVideo && attachment.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <PixelSpinner className="text-white" />
                  </div>
                )}
                {attachment.error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-danger/70 px-2 text-center text-xs font-bold text-white">
                    {attachment.error}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment.localId)}
                  aria-label={t('composer.removeImage')}
                  className="absolute right-1 top-1 rounded-md bg-black/70 p-1 text-white hover:bg-black"
                >
                  <X className="size-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {quoteOf && <QuoteCard post={quoteOf} />}

        {pollOptions && (
          <div className="mt-2 space-y-2 rounded-md border border-line p-3">
            {pollOptions.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  value={option}
                  maxLength={POLL_OPTION_MAX}
                  onChange={(event) =>
                    setPollOptions((current) =>
                      current
                        ? current.map((o, i) => (i === index ? event.target.value : o))
                        : current,
                    )
                  }
                  placeholder={t('poll.optionPlaceholder', { n: index + 1 })}
                  aria-label={t('poll.optionPlaceholder', { n: index + 1 })}
                  className="min-w-0 flex-1 rounded-md border border-line-strong bg-transparent px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
                />
                {index >= 2 && (
                  <button
                    type="button"
                    onClick={() =>
                      setPollOptions((current) => current?.filter((_, i) => i !== index) ?? null)
                    }
                    aria-label={t('poll.removeOption', { n: index + 1 })}
                    className="rounded-md p-1 text-ink-muted hover:bg-surface-hover"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              {pollOptions.length < POLL_MAX_OPTIONS && (
                <button
                  type="button"
                  onClick={() =>
                    setPollOptions((current) => (current ? [...current, ''] : current))
                  }
                  className="inline-flex items-center gap-1 text-[13px] font-bold text-brand-ink hover:underline"
                >
                  <Plus className="size-4" /> {t('poll.addOption')}
                </button>
              )}
              <label className="ml-auto flex items-center gap-2 text-[13px] text-ink-muted">
                {t('poll.duration')}
                <select
                  value={pollMinutes}
                  onChange={(event) => setPollMinutes(Number(event.target.value))}
                  className="rounded-md border border-line-strong bg-surface px-2 py-1 text-[13px] text-ink"
                >
                  {POLL_DURATIONS.map(({ minutes, label }) => (
                    <option key={minutes} value={minutes}>
                      {t(label)}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => setPollOptions(null)}
                className="text-[13px] font-bold text-danger hover:underline"
              >
                {t('poll.remove')}
              </button>
            </div>
          </div>
        )}

        {!replyTo && (
          <div className="mt-2">
            <ReplyPolicyPicker
              value={replyPolicy}
              onChange={setReplyPolicy}
              disabled={createPost.isPending}
            />
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-line pt-2">
          <IdentityPicker
            value={authorKind}
            onChange={pickIdentity}
            allowHuman={!isAgentAccount}
            verified={Boolean(verificationToken)}
            disabled={createPost.isPending}
          />
          {needsVerification && (
            <button
              type="button"
              onClick={() => setChallengeOpen(true)}
              className="text-[13px] font-bold text-brand-ink hover:underline"
            >
              {t('composer.needsProof')}
            </button>
          )}
        </div>

        <div className="mt-2 flex items-center justify-between pt-1">
          <div className="flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              accept={MEDIA_ACCEPT}
              multiple
              hidden
              onChange={(event) => {
                void addFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachments.length >= MAX_MEDIA || pollOptions !== null || hasVideo}
              aria-label={t('composer.addImage')}
              className="rounded-md p-2 text-brand-ink hover:bg-brand-ink/10 disabled:opacity-40"
            >
              <ImageIcon className="size-5" />
            </button>
            {/* 投票和图片、引用三选一：卡片叠在一起既难看也难读 */}
            <button
              type="button"
              onClick={() => setPollOptions((current) => (current ? null : ['', '']))}
              disabled={attachments.length > 0 || Boolean(quoteOf)}
              aria-label={t('poll.add')}
              aria-pressed={pollOptions !== null}
              className="rounded-md p-2 text-brand-ink hover:bg-brand-ink/10 disabled:opacity-40"
            >
              <BarChart3 className="size-5" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            {used > 0 && <CharCounter used={used} max={MAX_CHARS} />}
            <Button onClick={submit} disabled={!canSubmit} loading={createPost.isPending}>
              {replyTo ? t('composer.reply') : t('composer.publish')}
            </Button>
          </div>
        </div>
      </div>

      <ChallengeDialog
        open={challengeOpen}
        onClose={() => {
          setChallengeOpen(false)
          // 人类账号没验过就退回「人类」，免得发布按钮一直是灰的还不知道为什么；
          // agent 账号只能以 AI 发，留在 AI 上，旁边的「去验证」按钮会提示还差一步
          if (!verificationToken) setAuthorKind(isAgentAccount ? 'agent' : 'human')
        }}
        onVerified={(token) => {
          setVerificationToken(token)
          setAuthorKind('agent')
          setChallengeOpen(false)
          toast.show(t('composer.proofPassed'))
        }}
      />
    </div>
  )
}

/** 字数环：接近上限变黄，超出变红并显示剩余数 */
function CharCounter({ used, max }: { used: number; max: number }) {
  const t = useT()
  const remaining = max - used
  const ratio = Math.min(used / max, 1)
  const radius = 10
  const circumference = 2 * Math.PI * radius
  const color =
    remaining < 0 ? 'var(--color-danger)' : remaining <= 20 ? '#f5a623' : 'var(--color-brand-ink)'

  return (
    <div
      className="flex items-center gap-1"
      aria-label={t('composer.remaining', { count: remaining })}
    >
      {remaining <= 20 && (
        <span
          className={cn(
            'text-[13px] tabular-nums',
            remaining < 0 ? 'text-danger' : 'text-ink-muted',
          )}
        >
          {remaining}
        </span>
      )}
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
        <circle cx="12" cy="12" r={radius} fill="none" stroke="var(--color-line)" strokeWidth="2" />
        <circle
          cx="12"
          cy="12"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          transform="rotate(-90 12 12)"
        />
      </svg>
    </div>
  )
}

/** 视频附件上的进度条：上传有百分比，处理中是一条来回走的条 */
function VideoProgress({ label, fraction }: { label: string; fraction?: number }) {
  return (
    <div
      role="status"
      className="absolute inset-x-0 bottom-0 space-y-1 bg-black/70 px-3 py-2 text-[13px] font-bold text-white"
    >
      <span>{label}</span>
      <div className="h-1 overflow-hidden rounded-full bg-white/25">
        <div
          className={cn('h-full bg-brand', fraction === undefined && 'w-1/3 animate-pulse')}
          style={fraction === undefined ? undefined : { width: `${Math.round(fraction * 100)}%` }}
        />
      </div>
    </div>
  )
}
